/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/ExcelErrorModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useTranslation } from "react-i18next";
import styles from "./index.module.css";
import {
  DUPLICATE_USER_FIELD_EMAIL,
  DUPLICATE_USER_FIELD_NAME,
  EXCEL_HEADER_CORREO_CONTACTO,
  EXCEL_HEADER_CORREO_USUARIO,
} from "../../utils/excelValidation.js";

const EXCEL_ERROR_ROW_DETAILS_KEY = "pages.buyerCompanyForm.excelErrorRowDetails";

const DUPLICATE_USER_FIELD_LABEL_KEYS = {
  [DUPLICATE_USER_FIELD_NAME]: "pages.buyerCompanyForm.principalContact",
  [DUPLICATE_USER_FIELD_EMAIL]: "pages.buyerCompanyForm.email",
};

const joinMissingFields = (row) => row.missingFields.join(", ");
const detailValue = (row) => row.value;
const detailRfc = (row) => row.rfc;
const detailFieldValue = (row) => `${row.field}: ${row.value}`;
const detailCompanyIdentity = (row) =>
  `${row.razonSocial} - ${row.nombreComercial} - ${row.rfc}`;
const detailRequirementName = (row) => row.nombreRequerimiento;
const detailInvalidStatusFields = (row) =>
  row.invalidStatusFields.map((field) => field.field).join(", ");

const detailDuplicateUser = (row, t) => {
  const labelKey = DUPLICATE_USER_FIELD_LABEL_KEYS[row.field];
  const fieldDisplayName = labelKey ? t(labelKey) : row.field;
  return `${fieldDisplayName}: ${row.value}`;
};

const excelErrorItemKey = (row) =>
  [
    row.type,
    row.rowNumber,
    row.field,
    row.nombreRequerimiento,
    row.company,
    row.user,
    row.value,
    row.rfc,
  ]
    .filter((part) => part != null && part !== "")
    .join("-");

const importErrorKey = (importError) =>
  [importError.company, importError.user, importError.error]
    .filter((part) => part != null && part !== "")
    .join("-");

const rowsOfType = (invalidRows, type) =>
  invalidRows.filter((row) => row.type === type);

const groupExcelErrors = (invalidRows) => {
  const emailErrors = rowsOfType(invalidRows, "email");
  return {
    companyErrors: rowsOfType(invalidRows, "company"),
    userErrors: rowsOfType(invalidRows, "user"),
    rfcErrors: rowsOfType(invalidRows, "rfc"),
    companyEmailErrors: emailErrors.filter(
      (row) => row.field === EXCEL_HEADER_CORREO_CONTACTO,
    ),
    userEmailErrors: emailErrors.filter(
      (row) => row.field === EXCEL_HEADER_CORREO_USUARIO,
    ),
    stateErrors: rowsOfType(invalidRows, "state"),
    townErrors: rowsOfType(invalidRows, "town"),
    sizeErrors: rowsOfType(invalidRows, "size"),
    countryCodeErrors: rowsOfType(invalidRows, "countryCode"),
    phoneErrors: rowsOfType(invalidRows, "phone"),
    duplicateErrors: rowsOfType(invalidRows, "duplicate"),
    companyFieldsMismatchErrors: rowsOfType(invalidRows, "companyFieldsMismatch"),
    duplicateUserErrors: rowsOfType(invalidRows, "duplicateUser"),
    missingFieldsErrors: rowsOfType(invalidRows, "missingFields"),
    invalidUserErrors: rowsOfType(invalidRows, "invalidUser"),
    invalidCategoryErrors: rowsOfType(invalidRows, "invalidCategory"),
    invalidSubcategoryErrors: rowsOfType(invalidRows, "invalidSubcategory"),
    invalidStatusFieldsErrors: rowsOfType(invalidRows, "invalidStatusFields"),
    missingRequirementFieldsErrors: rowsOfType(
      invalidRows,
      "missingRequirementFields",
    ),
  };
};

const ExcelErrorSection = ({ titleKey, rows, getDetails, appendError }) => {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className={styles.errorSection}>
      <h4 className={styles.errorSectionTitle}>
        {t(titleKey)}:
      </h4>
      <ul className={styles.errorList}>
        {rows.map((row) => {
          const rowLabel = t(EXCEL_ERROR_ROW_DETAILS_KEY, {
            rowNumber: row.rowNumber,
            details: getDetails(row, t),
          });
          return (
            <li key={excelErrorItemKey(row)} className={styles.errorItem}>
              {appendError ? `${rowLabel} - ${row.error}` : rowLabel}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const ExcelErrorModal = ({
  open,
  onClose,
  errorMessage,
  invalidRows = [],
  importErrors = [],
}) => {
  const { t } = useTranslation();

  const handleClose = () => {
    onClose();
  };

  const organizedErrors = groupExcelErrors(invalidRows);
  const errorSections = [
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeMissingCompanyFields",
      rows: organizedErrors.companyErrors,
      getDetails: joinMissingFields,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeMissingUserFields",
      rows: organizedErrors.userErrors,
      getDetails: joinMissingFields,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidRFC",
      rows: organizedErrors.rfcErrors,
      getDetails: detailRfc,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidCompanyEmail",
      rows: organizedErrors.companyEmailErrors,
      getDetails: detailValue,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidUserEmail",
      rows: organizedErrors.userEmailErrors,
      getDetails: detailValue,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidState",
      rows: organizedErrors.stateErrors,
      getDetails: detailValue,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidTown",
      rows: organizedErrors.townErrors,
      getDetails: detailValue,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidSize",
      rows: organizedErrors.sizeErrors,
      getDetails: detailValue,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidCountryCode",
      rows: organizedErrors.countryCodeErrors,
      getDetails: detailValue,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidPhone",
      rows: organizedErrors.phoneErrors,
      getDetails: detailValue,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeDuplicateCompany",
      rows: organizedErrors.duplicateErrors,
      getDetails: detailFieldValue,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeCompanyFieldsMismatch",
      rows: organizedErrors.companyFieldsMismatchErrors,
      getDetails: detailCompanyIdentity,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeDuplicateUser",
      rows: organizedErrors.duplicateUserErrors,
      getDetails: detailDuplicateUser,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeMissingFields",
      rows: organizedErrors.missingFieldsErrors,
      getDetails: joinMissingFields,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidUser",
      rows: organizedErrors.invalidUserErrors,
      getDetails: detailValue,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidCategory",
      rows: organizedErrors.invalidCategoryErrors,
      getDetails: detailValue,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidSubcategory",
      rows: organizedErrors.invalidSubcategoryErrors,
      getDetails: detailValue,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeInvalidStatusFields",
      rows: organizedErrors.invalidStatusFieldsErrors,
      getDetails: detailInvalidStatusFields,
      appendError: true,
    },
    {
      titleKey: "pages.buyerCompanyForm.excelErrorTypeMissingRequirementFields",
      rows: organizedErrors.missingRequirementFieldsErrors,
      getDetails: detailRequirementName,
      appendError: true,
    },
  ];

  if (!open) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            {t("pages.buyerCompanyForm.excelFileErrorTitle")}
          </h3>
          <button className={styles.closeButton} onClick={handleClose}>
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.modalSubtitle}>
            {t("pages.buyerCompanyForm.excelFileErrorSubtitle")}
          </p>

          {/* General Error Message */}
          {errorMessage && invalidRows.length === 0 && (
            <div className={styles.detailedErrors}>
              <div className={styles.errorSection}>
                <h4 className={styles.errorSectionTitle}>
                  {t("pages.buyerCompanyForm.excelErrorTypeGeneral")}:
                </h4>
                <ul className={styles.errorList}>
                  <li className={styles.errorItem}>{errorMessage}</li>
                  {importErrors.map((importError) => (
                    <li
                      key={importErrorKey(importError)}
                      className={styles.errorItem}
                    >
                      {t("pages.buyerCompanyForm.excelImportErrorItem", {
                        company: importError.company,
                        user: importError.user ? ` / ${importError.user}` : "",
                        error: importError.existingUser?.nombre
                          ? t("pages.buyerCompanyForm.excelExistingUserError", {
                              name: importError.existingUser.nombre,
                            })
                          : importError.error,
                      })}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {invalidRows.length > 0 && (
            <div className={styles.detailedErrors}>
              {errorSections.map((section) => (
                <ExcelErrorSection
                  key={section.titleKey}
                  titleKey={section.titleKey}
                  rows={section.rows}
                  getDetails={section.getDetails}
                  appendError={section.appendError}
                />
              ))}
            </div>
          )}
        </div>

        <div className={styles.modalActions}>
          <button
            className={`${styles.modalButton} ${styles.closeButtonAction}`}
            onClick={handleClose}
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExcelErrorModal;
